/**
 * 使用量（/money/usage）: 今月の合計・今日の分・残高があと何日もつか・種類ごと（量と額）・プロジェクトごと・日ごとの推移。
 * サーバー: GET /v1/money/usage?period=&refresh=・GET /v1/money/usage/daily?period=（money/usage.ts・usageViews.ts）・GET /v1/money/prepaid（何日もつか）。
 *
 * 2026-10-07 console/usage-view（founder「クリエイターは常にダッシュボードで自分のクレジット残高や、DB やサーバー、転送料などの Usage を知りたいはず」）:
 *   - 以前は「部品」（Saves & data・Hosting …）別の額だけ = 量が無く、部品の名前が利用者の物の名前と合っていなかった。
 *     いまはサーバーの種類（データベース・サーバーの機能・アプリの配信 …）ごとに「どれだけ使ったか（量）」と「いくら」
 *   - 「Metered cost（切り上げ前）」のタイルは外した（中の計算の段階 = 利用者の判断に要らない）。合計は残高から実際に引いた額（charged + 定額の機能）
 *   - いつの数字か: 最終の更新（asOf）と、新しい利用が入るまでの目安（サーバーの updatesWithinMinutes）を文で
 *   - お金の決まり（料金の 1 行・その場で引いて月末は返すだけ）は Money の PriceNotes.tsx（前払い残高の画面・サイト・CLI・MCP と同じ文）。
 *     この画面にはその文を置かず、前払い残高の「料金のしくみ」の段へのリンクだけ（2026-10-18 design/rauno-dashboard2）
 * 古い本体（byCategory 等が無い）は今までの部品の内訳で出す（usageView.ts usageRows）。
 *
 * Kumo の定額の機能（2026-09-28 money/slug-redirect・転送 月 $1）: 原価ではなく Kumo の料金なので、種類ごと（原価どおり）の棒・日別のグラフには混ぜず、
 * 「Kumo の定額の機能」の別の欄で見せる（サーバーも serviceFees と別の欄で返す）。
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ago, ErrorState, Empty, PageHeader, Spinner, StatTile } from '../../components/ui';
import { useResource } from '../../hooks';
import { intlLocale, useT } from '../../i18n';
import { money } from '../../money';
import type { DailyUsage, PrepaidView, UsageSummary } from '../../moneyApi';
import { prepaidTone, recentPeriods } from '../../moneyLogic';
import { daysSoFar, runwayView, todayMicros, usageRows } from '../../usageView';
import { AddonsCard, ByKindCard, ByProjectCard, DailyBars, microsMoney, type ServiceFeeLine } from './UsageParts';

export { componentLabel, meterLabel } from './UsageParts';

type UsageWithFees = UsageSummary & { serviceFees?: ServiceFeeLine[]; serviceFeesCharged?: number };

export function UsagePage() {
  const periods = useMemo(() => recentPeriods(new Date(), 6), []);
  const [period, setPeriod] = useState(periods[0]!);
  const [refreshing, setRefreshing] = useState(0);
  // refresh=1 は計測のメモリの束を流して集計し直す（サーバーの流量制限は 30 回/時）ので「今すぐ更新」を押したときだけ付ける。
  // n はキャッシュを避けて読み直させるための番号（サーバーは知らない query を無視する）
  const r = useResource<UsageSummary>(`/v1/money/usage?period=${period}${refreshing ? `&refresh=1&n=${refreshing}` : ''}`);
  const daily = useResource<DailyUsage>(`/v1/money/usage/daily?period=${period}&n=${refreshing}`);
  const prepaid = useResource<PrepaidView>('/v1/money/prepaid');
  const { t } = useT();

  const now = new Date();
  const current = period === periods[0];
  const u = r.data as UsageWithFees | undefined;
  const fees = u?.serviceFees ?? [];
  const feesCharged = u?.serviceFeesCharged ?? 0;
  const rows = u ? usageRows(u) : [];
  const days = daysSoFar(period, now);
  // 「少ない」の線はサーバーがいま効かせている線（PrepaidPage と同じ判定・moneyLogic.ts prepaidTone）
  const tone = prepaid.data ? prepaidTone(prepaid.data) : null;

  return (
    <>
      <PageHeader title={t('use.title')}>
        <label className="field field-inline">
          <span className="sr-only">{t('use.month')}</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periods.map((p) => (
              <option key={p} value={p}>
                {new Date(`${p}-01T00:00:00Z`).toLocaleDateString(intlLocale(), { year: 'numeric', month: 'long', timeZone: 'UTC' })}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRefreshing((n) => n + 1)} disabled={r.loading}>
          {t('use.refresh')}
        </button>
      </PageHeader>
      {/* リードの 1 文 + 「料金のしくみ」へのリンク（2026-10-18 design/rauno-dashboard2・rauno-audit.md 使用額の行）。
          以前はここにも PriceNotes（原価どおり・税・月末は戻すだけ）の段落 = 前払い残高の画面と同じ説明が 2 か所にあった。
          説明は前払い残高の「料金のしくみ」の段（PrepaidPage.tsx PricingSection・id="prepaid-pricing"）の 1 か所。この画面は「どこに使ったか」だけ */}
      <p className="page-lead muted">
        {t('use.lead')}
        {t('confirmPlain.sentenceGap')}
        <Link to="/money/prepaid#prepaid-pricing">{t('pp.howPricing')}</Link>
      </p>

      {prepaid.data && (tone === 'owed' || tone === 'low') && (
        <div className={`alert ${tone === 'owed' ? 'alert-error' : 'alert-warn'}`} role="note">
          <strong>{tone === 'owed' ? t('pp.owedTitle') : t('pp.lowTitle')}</strong>
          <span>
            {tone === 'owed'
              ? t('pp.owedBody', { owed: money(prepaid.data.owed, prepaid.data.currency) })
              : typeof prepaid.data.daysLeft === 'number'
                ? t('pp.lowBodyDays', { days: prepaid.data.daysLeft })
                : t('pp.lowBody')}
          </span>
          <Link to="/money/prepaid" className="btn btn-sm btn-ghost">
            {t('pp.topUp')}
          </Link>
        </div>
      )}

      {r.loading && !u && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('use.title')} onRetry={r.reload} />}
      {u && (
        <>
          <section className="tiles">
            {/* 合計 = 残高から実際に引いた額（原価の分 + 定額の機能）。月末に戻った額は下の行と「戻った額」のタイルで別に見せる */}
            <StatTile label={t(current ? 'use.thisMonth' : 'use.monthTotal')} value={money(u.charged + feesCharged, 'usd')} hint={t('use.totalHint')} tone="accent" />
            {current && daily.data && <StatTile label={t('use.today')} value={microsMoney(todayMicros(daily.data, now))} hint={t('use.todayHint')} />}
            {current && prepaid.data && <RunwayTile p={prepaid.data} now={now} warn={tone === 'low' || tone === 'owed'} />}
            {u.refunded > 0 && <StatTile label={t('use.returned')} value={money(u.refunded, 'usd')} hint={t('use.returnedHint')} />}
            {fees.length > 0 && feesCharged > 0 && <StatTile label={t('use.addonsTile')} value={money(feesCharged, 'usd')} hint={t('use.addonsTileHint')} />}
          </section>
          <p className="muted small usage-meta">
            <span className={`pill ${u.status === 'closed' ? 'pill-ok' : ''}`}>{t(`use.status.${u.status}`)}</span>{' '}
            {u.asOf ? t('use.freshAgo', { ago: ago(u.asOf) }) : t('use.freshNever')}
            {u.status !== 'closed' && ` ${freshWithin(t, u.updatesWithinMinutes)}`}
          </p>

          {fees.length > 0 && <AddonsCard fees={fees} />}

          {rows.length === 0 ? (
            <section className="card">
              <Empty title={t('use.emptyTitle')}>
                <p>{t('use.emptyBody')}</p>
              </Empty>
            </section>
          ) : (
            <>
              <ByKindCard rows={rows} days={days} sharedMicros={u.fixedCostMicros} returnedCents={u.refunded} />
              {u.byProject && u.byProject.length > 0 && <ByProjectCard projects={u.byProject} accountWide={u.fixedCostMicros > 0 || fees.length > 0} />}
              <section className="card">
                <div className="card-head">
                  <h2 className="card-title">{t('use.byDay')}</h2>
                  <span className="muted small">
                    {t('use.byDayNote')}
                    {fees.length > 0 && ` ${t('use.addonsDayNote')}`}
                  </span>
                </div>
                {daily.error && <ErrorState error={daily.error} what={t('use.byDay')} onRetry={daily.reload} />}
                {daily.data && (
                  <DailyBars
                    data={daily.data}
                    order={rows.map((x) => x.key)}
                    // 種類の内訳と日ごとの種類は同じ新しい本体から来る。片方だけ古い（日ごとに byCategory が無い）なら部品で積む
                    source={rows[0]?.source === 'category' && daily.data.categories ? 'category' : 'component'}
                  />
                )}
              </section>
            </>
          )}
        </>
      )}
    </>
  );
}

/** 「新しい利用は約 1 時間以内に反映されます」。古い本体（分が無い）は毎時の集計（サーバーの既定）として言う */
function freshWithin(t: ReturnType<typeof useT>['t'], minutes: number | undefined): string {
  const m = typeof minutes === 'number' && minutes > 0 ? minutes : 60;
  return m === 60 ? t('use.freshHour') : t('use.freshWithin', { count: m });
}

/** 残高があと何日もつか（usageView.ts runwayView の言い方）。値の下に残高と「どれくらい当てになるか」 */
function RunwayTile({ p, now, warn }: { p: PrepaidView; now: Date; warn: boolean }) {
  const { t } = useT();
  const v = runwayView(p, now);
  const balance = money(p.balance, p.currency);
  const value =
    v.kind === 'out' ? t('pp.runwayZero') : v.kind === 'noUsage' ? t('pp.runwayIdle') : v.kind === 'unknown' ? balance : t('pp.runwayDays', { count: v.days });
  const hint =
    v.kind === 'early'
      ? t('use.runwayEarly', { balance, count: v.daysOfData })
      : v.kind === 'noUsage'
        ? t('use.runwayIdleHint', { balance })
        : v.kind === 'days'
          ? t('use.runwayHint', { balance })
          : t('use.balanceHint', { amount: balance });
  return <StatTile label={t(v.kind === 'unknown' ? 'pp.balance' : 'pp.runway')} value={value} hint={hint} tone={warn ? 'warn' : undefined} />;
}
